import { useEffect, useState } from 'react';

import * as wikiApi from '@/features/wiki/api';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';

/** The wiki-attachment id embedded in a `/wiki-attachments/<id>` URL (or a bare uuid), else null for
 *  an external src the browser can load directly. Wiki attachments are served through the API (Bearer
 *  token required — a plain <img> would 401), so this decides whether an auth'd fetch is needed. */
export const wikiAttachmentIdFromSrc = (src: string): string | null => {
  if (!src) return null;
  const marker = '/wiki-attachments/';
  const i = src.indexOf(marker);
  if (i !== -1) {
    const rest = src.slice(i + marker.length).split(/[?#/]/)[0];
    if (/^[0-9a-f-]{16,}$/i.test(rest)) return rest;
  }
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(src)) return src;
  return null;
};

/** Fetch a wiki attachment's bytes with auth, exposing an object URL + the blob's MIME type. External
 *  (non-attachment) srcs are used verbatim. The object URL is revoked on unmount / src change. */
export function useWikiBlob(src: string) {
  const [state, setState] = useState<{ url: string | null; type: string; error: boolean }>({ url: null, type: '', error: false });
  useEffect(() => {
    const id = wikiAttachmentIdFromSrc(src);
    if (!id) {
      setState({ url: src, type: '', error: false });
      return;
    }
    let cancelled = false;
    let obj: string | null = null;
    setState({ url: null, type: '', error: false });
    wikiApi
      .fetchWikiAttachmentBlob(id)
      .then((blob) => {
        if (cancelled) return;
        obj = URL.createObjectURL(blob);
        setState({ url: obj, type: blob.type || '', error: false });
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, type: '', error: true });
      });
    return () => {
      cancelled = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [src]);
  return state;
}

/**
 * Renders a wiki attachment inline: a token-authed <video controls> when the blob is a video, else an
 * <img>. Mirrors the description editor's AuthMedia (blob-MIME sniff + onError→download fallback) but
 * against the wiki-attachment stream path. `width` (px) drives the resizable node view.
 */
export function WikiMedia({ src, alt, width, onLoad }: { src: string; alt?: string; width?: number | null; onLoad?: (w: number, h: number) => void }) {
  const { url, type, error } = useWikiBlob(src);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const isVideo = type.startsWith('video/') || (!type && /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(src));
  const style = width ? { width: `${width}px`, maxWidth: '100%' } : undefined;

  if (error || failed)
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-surface-inset px-2 py-1 text-[12px] text-fg-faint">
        {isVideo ? 'This video can’t be previewed here' : 'Media unavailable'}
        {url ? (
          <a href={url} download className="text-fg-soft underline underline-offset-2">
            download
          </a>
        ) : null}
      </span>
    );
  if (!url)
    return (
      <span className={cn('inline-flex h-40 items-center justify-center rounded-lg bg-surface-inset', width ? '' : 'w-64')} style={style}>
        <Spinner className="h-4 w-4 text-fg-faint" />
      </span>
    );
  if (isVideo) return <video src={url} controls playsInline preload="metadata" style={style} onError={() => setFailed(true)} className="rounded-lg ring-1 ring-line" />;
  return (
    <img
      src={url}
      alt={alt ?? ''}
      style={style}
      onError={() => setFailed(true)}
      onLoad={(e) => onLoad?.(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
      className="block rounded-lg ring-1 ring-line"
    />
  );
}
